import React, { Fragment, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Types
import UserRoles from 'types/UserRoles';
// Store
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
// Actions
import { inviteOwner, inviteStaff, inviteAdministrator } from 'store/user/userAsync';
// Selectors
import { selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import { selectLoading } from 'store/user/userSelectors';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Checkbox, Input, Select } from 'components/Controls';
import { Button } from 'components/Buttons';
// Utilities
import { isRequired, isEmail } from 'utilities/validation';
// i18next
import { useTranslation } from 'react-i18next';

interface IFormData {
  name: string;
  surname: string;
  email: string;
  group: UserRoles;
  organization?: string;
  cloneQuizzes?: boolean;
  cloneMedia?: boolean;
}

type Props = {
  open: boolean;
  onClose: () => void;
}

const InviteUserForm:React.FC<Props> = ({
  // Props
  open, onClose
}) => {
  const { t } = useTranslation();
  // Dispatch
  const dispatch = useAppDispatch();
  // State
  const currentUser = useAppSelector(selectCurrentUser);
  const loaidng:boolean = useAppSelector(selectLoading);

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);

  const { control, handleSubmit, formState: { errors }, watch, setValue } = useForm<IFormData>({
    defaultValues: {
      group: isAdmin ? UserRoles.Admin : UserRoles.Staff,
      name: '',
      surname: '',
      email: '',
      organization: '',
      cloneQuizzes: false,
      cloneMedia: false
    }
  });

  const onSubmit = handleSubmit(async (data:IFormData) => {
    const { cloneQuizzes, cloneMedia, organization, ...otherData } = data;
    const nextData:IFormData = { ...otherData };
    if ( [UserRoles.Admin, UserRoles.Owner].includes(data.group) ) nextData['organization'] = organization || 'Internal';
    if ( data.group === UserRoles.Owner && cloneQuizzes ) nextData['cloneQuizzes'] = cloneQuizzes;
    if ( data.group === UserRoles.Owner && cloneMedia ) nextData['cloneMedia'] = cloneMedia;
    try {
      if ( data.group === UserRoles.Admin ) await dispatch(inviteAdministrator(nextData)).unwrap();
      if ( data.group === UserRoles.Owner ) await dispatch(inviteOwner(nextData)).unwrap();
      if ( data.group === UserRoles.Staff ) await dispatch(inviteStaff(nextData)).unwrap();
      onClose();
    } catch(error){}
  });

  const groupWatch = watch('group');

  useEffect(() => {
    if (groupWatch !== UserRoles.Owner) {
      setValue('organization', '');
      setValue('cloneQuizzes', false);
      setValue('cloneMedia', false);
    }
    // eslint-disable-next-line
  }, [groupWatch]);

  return (
    <Dialog size="sm" open={open} onClose={onClose}>
      <form className="flex flex-col overflow-y-auto" onSubmit={onSubmit} noValidate>
        <DialogTitle title={t('dialogs.inviteUser.title')} onClose={onClose} />
        <DialogContent>
          {isAdmin ? (
            <Controller
              control={control} name="group" rules={{ required: isRequired }}
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t('labels.group')} id="group" name="group" value={value}
                  required={true}
                  onChange={onChange}
                  error={Boolean(errors.group)}
                  errorText={errors.group ? errors.group.message : ''}
                  options={[
                    { value: UserRoles.Admin, label: t('labels.admin') },
                    { value: UserRoles.Owner, label: t('labels.owner') },
                  ]}
                />
              )}
            />
          ) : null}
          <Controller
            control={control} name="name" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <Input
                label={t('labels.name')} id="name" name="name" value={value}
                required={true}
                onChange={onChange}
                error={Boolean(errors.name)}
                errorText={errors.name ? errors.name.message : ''}
              />
            )}
          />
          <Controller
            control={control} name="surname" rules={{ required: isRequired }}
            render={({ field: { value, onChange } }) => (
              <Input
                label={t('labels.surname')} id="surname" name="surname" value={value}
                required={true}
                onChange={onChange}
                error={Boolean(errors.surname)}
                errorText={errors.surname ? errors.surname.message : ''}
              />
            )}
          />
          <Controller
            control={control} name="email" rules={{ required: isRequired, pattern: isEmail }}
            render={({ field: { value, onChange } }) => (
              <Input
                label={t('labels.email')} id="email" type="email" name="email" value={value}
                required={true}
                onChange={onChange}
                error={Boolean(errors.email)}
                errorText={errors.email ? errors.email.message : ''}
              />
            )}
          />
          {groupWatch === UserRoles.Owner ? (
            <Fragment>
              <Controller
                control={control} name="organization" rules={{ required: isRequired }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('labels.organization')} id="organization" name="organization"
                    required={true}
                    error={Boolean(errors.organization)}
                    errorText={errors.organization ? errors.organization.message : ''}
                  />
                )}
              />
              <Controller
                control={control} name="cloneQuizzes"
                render={({ field: { value, ...otherFields } }) => (
                  <Checkbox
                    {...otherFields}
                    checked={value || false}
                    label={t('dialogs.inviteUser.cloneQuizzesLabel')}
                    id="cloneQuizzes"
                  />
                )}
              />
              <Controller
                control={control} name="cloneMedia"
                render={({ field: { value, ...otherFields } }) => (
                  <Checkbox
                    {...otherFields}
                    checked={value || false}
                    label={t('dialogs.inviteUser.cloneMediaLabel')}
                    id="cloneMedia"
                  />
                )}
              />
            </Fragment>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
          >{t('buttons.cancel')}</Button>
          <Button
            className="ml-2"
            variant="contained"
            type="submit"
            loading={loaidng}
          >{t('buttons.invite')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default InviteUserForm;
